import { Component, inject, signal } from '@angular/core';
import { GroupInformationComponent } from './components/group-information/group-information.component';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { SearchFieldComponent } from '../../shared/components/search-field/search-field.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatCardModule } from '@angular/material/card';
import { TableComponent } from '../../shared/components/table/table.component';
import { MatButtonModule } from '@angular/material/button';
import { PageEvent } from '@angular/material/paginator';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, Observable, switchMap, take } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { StatusLabelComponent } from '../../shared/components/status-label/status-label.component';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  GroupData,
  WebsiteExtendedWithOriginVhost,
} from '../../shared/models/group-overview.model';
import { MatRippleModule } from '@angular/material/core';
import { GroupUsageComponent } from './components/group-usage/group-usage.component';
import { MatDialog } from '@angular/material/dialog';
import { MigrateGroupDialogComponent } from './dialogs/migrate-group-dialog/migrate-group-dialog.component';
import { WebsiteGroupsService } from '../../core/services/website-groups.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StatusModalService } from '../../core/services/status-modal.service';
import { SnackbarComponent } from '../../shared/components/snackbar/snackbar.component';
import { AddWebsiteDialogComponent } from './dialogs/add-website-dialog/add-website-dialog.component';
import {
  CreateWebsiteDTO,
  CreateWebsiteAliasDTO,
  CreateWebsiteSubdomainDTO,
} from '../../shared/models/website-create.models';
import { Website, WebsiteEnvironment } from '../../shared/models/website.models';
import { WebsitesService } from '../../core/services/websites.service';
import { TemporaryWPCredentials } from '../../shared/models/wordpress.models';
import { WPCliService } from '../../core/services/wp-cli.service';
import { ChangePlanDialogComponent } from './dialogs/change-plan-dialog/change-plan-dialog.component';
import { SystemUserService } from '../../core/services/system-user.service';

@Component({
  selector: 'app-group-overview',
  imports: [
    GroupInformationComponent,
    SearchFieldComponent,
    FontAwesomeModule,
    MatCardModule,
    TableComponent,
    MatButtonModule,
    AsyncPipe,
    StatusLabelComponent,
    MatTableModule,
    MatTooltipModule,
    RouterLink,
    MatRippleModule,
    GroupUsageComponent,
  ],
  templateUrl: './group-overview.component.html',
  styleUrl: './group-overview.component.scss',
})
export class GroupOverviewComponent {
  private groupsService = inject(WebsiteGroupsService);

  private websitesService = inject(WebsitesService);

  private wpCliService = inject(WPCliService);

  private systemUserService = inject(SystemUserService);

  private route = inject(ActivatedRoute);

  private dialog = inject(MatDialog);

  private snackbar = inject(MatSnackBar);

  private statusModalService = inject(StatusModalService);

  readonly groupData: GroupData = this.route.snapshot.data['groupData'];

  types: Record<string, string> = {
    MAIN: 'Main',
    SUB_WEBSITE: 'Site',
    ALIAS: 'Alias',
    STAGING: 'Staging',
  };

  filters = signal<{ page: number; pageSize: number; domainSearch?: string }>({
    page: 1,
    pageSize: 10,
  });

  filters$ = toObservable(this.filters);

  totalWebsites = signal(this.groupData?.websites?.length || 0);

  websites$ = this.filters$.pipe(
    map((filters) => {
      const records = this.groupData.websites ?? [];

      // Search
      const filteredRecords = filters.domainSearch
        ? records.filter((record) =>
            record.domain.toLowerCase().includes(filters.domainSearch!.toLowerCase()),
          )
        : records;

      // Pagination
      const start = (filters.page - 1) * filters.pageSize;
      const end = start + filters.pageSize;

      return filteredRecords.slice(start, end);
    }),
  );

  websiteColumns = [
    {
      columnDef: 'id',
      header: 'ID',
      cell: (element: WebsiteExtendedWithOriginVhost) => `${element.id}`,
    },
    {
      columnDef: 'type',
      header: 'Type',
      cell: (element: WebsiteExtendedWithOriginVhost) => `${this.types[element.type] || 'N/A'}`,
    },
    {
      columnDef: 'website',
      header: 'Website',
      cell: (element: WebsiteExtendedWithOriginVhost) => `${element.domain}`,
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: (element: WebsiteExtendedWithOriginVhost) => `${element.status}`,
      useTemplate: true,
    },
    {
      columnDef: 'php',
      header: 'PHP Version',
      cell: () => `N/A`,
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  handlePageEvent(event: PageEvent) {
    this.filters.update((f) => ({ ...f, page: event.pageIndex + 1 }));
  }

  migrateGroup(): void {
    this.dialog
      .open(MigrateGroupDialogComponent, {
        data: { currentServerId: this.groupData.server.id },
        autoFocus: false,
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(({ server }) =>
          this.groupsService.migrateGroup(
            this.groupData.websiteGroup.id,
            this.groupData.server.id,
            server,
          ),
        ),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: 'Group migration started',
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error ?? 'Group migration could not be started.',
          });
        },
      });
  }

  addWebsite(): void {
    this.dialog
      .open(AddWebsiteDialogComponent, {
        data: {
          websites: this.groupData.websites,
          subTitle: this.groupData.websiteGroup.mainWebsite?.domain,
          owner: this.groupData.systemUser.name,
          server: this.groupData.server,
          plan: this.groupData.hostingPlan.name,
          groupId: this.groupData.websiteGroup.id,
        },
        autoFocus: false,
      })
      .afterClosed()
      .pipe(
        take(1),
        filter(Boolean),
        switchMap(
          (result: {
            website: CreateWebsiteDTO | CreateWebsiteAliasDTO | CreateWebsiteSubdomainDTO;
            wordpress?: TemporaryWPCredentials;
          }) => {
            const create$ = result.wordpress
              ? this.wpCliService
                  .saveTempCredentials(result.wordpress)
                  .pipe(switchMap(() => this.createWebsite(result.website)))
              : this.createWebsite(result.website);

            return create$.pipe(
              map((createResult) => ({
                createResult,
                website: result.website,
              })),
            );
          },
        ),
      )
      .subscribe({
        next: ({ website }) =>
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Adding website ${website.domain} started`,
              highlights: [website.domain],
            },
          }),
        error: (error) =>
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: error.error?.error, // To be checked
            },
          }),
      });
  }

  changePlan(): void {
    this.dialog
      .open(ChangePlanDialogComponent, {
        data: {
          subTitle: this.groupData.websiteGroup.mainWebsite?.domain,
          currentPlan: this.groupData.hostingPlan,
        },
        autoFocus: false,
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(({ plan }) =>
          this.systemUserService.changePlan(this.groupData.systemUser.id, +plan),
        ),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: 'Plan change started',
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error ?? 'Something went wrong while starting plan change!',
          });
        },
      });
  }

  onSearch(text: string) {
    this.filters.update((f) => ({ ...f, domainSearch: text }));
  }

  private createWebsite(
    website: CreateWebsiteDTO | CreateWebsiteAliasDTO | CreateWebsiteSubdomainDTO,
  ): Observable<Website> {
    if (this.isAlias(website)) {
      return this.websitesService.createWebsiteAlias(website);
    } else if (this.isSubdomain(website)) return this.websitesService.createSubwebsite(website);
    else return this.websitesService.createWebsite(website);
  }

  private isAlias(
    website: CreateWebsiteDTO | CreateWebsiteAliasDTO | CreateWebsiteSubdomainDTO,
  ): website is CreateWebsiteAliasDTO {
    return (website as CreateWebsiteAliasDTO).websiteEnvironment === WebsiteEnvironment.ALIAS;
  }

  private isSubdomain(
    website: CreateWebsiteDTO | CreateWebsiteAliasDTO | CreateWebsiteSubdomainDTO,
  ): website is CreateWebsiteSubdomainDTO {
    return (website as CreateWebsiteAliasDTO).websiteEnvironment === WebsiteEnvironment.SUB;
  }
}
